import useFormContext from "@mittwald/flow-components/dist/components/Form/hooks/useForm/context";
import type { SelectOptions } from "@mittwald/flow-components/dist/components/Select/types";
import { TextField } from "@mittwald/flow-components/dist/components/TextField";
import { UniversalBoundary } from "@mittwald/flow-components/dist/components/UniversalBoundary";
import React, { useEffect } from "react";
import Customer from "../../../../../../../../model/customer/Customer";
import {
  ProjectList,
  ProSpaceProject,
} from "../../../../../../../../model/project";
import ServerList from "../../../../../../../../model/server/ServerList";
import type {
  RelocationFormData,
  RelocationReferrer,
} from "../../../../../RelocationRequestWidget";
import Select from "@mittwald/flow-components/dist/components/Select/Select";
import { TariffType } from "../../../TariffStep/utils/TariffUtils";

interface ProjectSelectProps {
  isLoggedIn: boolean;
  referrer: RelocationReferrer;
}

export const ProjectSelect: React.FC<ProjectSelectProps> = (props) => {
  const { isLoggedIn, referrer } = props;
  const form = useFormContext<RelocationFormData>();
  const customerId = form.watch("target.organisationUUID");
  const product = form.watch("target.product") as TariffType;
  const selectTariff = form.watch("target.projectName");

  const serverList = ServerList.useLoad().useItems();
  const projectList = ProjectList.useLoad().useItems();

  const serverOptions: SelectOptions = serverList.map((server) => ({
    label: { text: server.data.description },
    value: server.data.shortId,
    payload: server.data.customerId,
  }));
  const projectOptions: SelectOptions = projectList
    .filter((project) => project instanceof ProSpaceProject)
    .map((project) => ({
      label: { text: project.data.description },
      value: project.data.shortId,
      payload: project.data.customerId,
    }));

  const tariffSelectOptions: SelectOptions = [];

  if (referrer === "wizard") {
    tariffSelectOptions.push(...serverOptions, ...projectOptions);
  } else {
    if (product === TariffType.SPACE_SERVER) {
      tariffSelectOptions.push(...serverOptions);
    } else if (product === TariffType.PRO_SPACE) {
      tariffSelectOptions.push(...projectOptions);
    }
  }

  useEffect(() => {
    void (async () => {
      if (
        tariffSelectOptions.length === 1 &&
        tariffSelectOptions[0]?.value &&
        !form.watch("target.projectName")
      ) {
        form.setValue("target.projectName", tariffSelectOptions[0].value, {
          shouldValidate: true,
        });
        form.setValue(
          "target.organisationUUID",
          tariffSelectOptions[0].payload,
        );

        const customer = await Customer.loadById(
          tariffSelectOptions[0].payload,
        );
        form.setValue("target.organisation", customer.data.name);
      }
    })();
  }, [customerId]);

  useEffect(() => {
    if (selectTariff) {
      const selectedServer = serverList.find(
        (server) => server.data.shortId === selectTariff,
      );

      if (selectedServer) {
        form.setValue("target.product", TariffType.SPACE_SERVER);
      } else {
        form.setValue("target.product", TariffType.PRO_SPACE);
      }
    }
  }, [selectTariff]);

  return isLoggedIn ? (
    <UniversalBoundary
      loadingView={
        <Select.View
          disabled
          label="projectSelect"
          options={[]}
          placeholder="projectSelect.placeholder"
        />
      }
    >
      <Select
        label="projectSelect"
        name="target.projectName"
        options={tariffSelectOptions}
        placeholder="projectSelect.placeholder"
        rules={{ required: true }}
        onChangeWithPayload={(_, payload) => {
          form.setValue("target.organisationUUID", payload.payload);

          void Customer.loadById(payload.payload).then((customer) => {
            form.setValue("target.organisation", customer.data.name);
          });
        }}
      />
    </UniversalBoundary>
  ) : (
    <TextField
      infoMessage={{ message: "projectShortId.infoMessage" }}
      label="projectShortId"
      name="target.projectName"
      placeholder="projectShortId.placeholder"
      rules={{
        required: true,
        pattern: /^(p-[A-Za-z0-9]+)/,
      }}
    />
  );
};
